import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * ALPINULIN Set-Kauf über Komponenten-Einzelzeilen.
 *
 * Hintergrund: Shopify-Bundle-Produkte können keine Selling Plans tragen
 * (Fixed Bundles ✕ Subscriptions). Damit ein Set trotzdem abonnierbar ist UND
 * der Fulfiller echte Einzelpositionen bekommt, legt dieser Button die
 * Komponenten einzeln in den Warenkorb — jede mit demselben Selling Plan.
 * Recharge fasst Subscriptions gleicher Adresse und gleichem Termin zu einer
 * Lieferung zusammen.
 *
 * Die Zeilen tragen eine Line-Item-Property (Default `_Set`), damit Cart,
 * Order und Fulfillment die Zugehörigkeit erkennen.
 */
class BundleAddMulti extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-bundle-add-button]');
    this.button?.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#handleClick);
  }

  #handleClick = (event) => {
    event.preventDefault();
    this.#addToCart();
  };

  #buildItems() {
    let items = [];
    try {
      items = JSON.parse(this.dataset.items || '[]');
    } catch (error) {
      return [];
    }

    const checked = this.querySelector('input[data-bundle-plan]:checked');
    const wantsSubscription = checked?.value === 'sub';
    const propertyKey = this.dataset.propertyKey || '_Set';
    const setName = this.dataset.setName || '';

    return items
      .filter((item) => item.id)
      .map((item) => {
        const line = {
          id: Number(item.id),
          quantity: Number(item.quantity) || 1,
          properties: setName ? { [propertyKey]: setName } : {},
        };
        // Jede Komponente bringt ihre eigene Plan-ID mit (Recharge legt pro
        // Produkt eigene Gruppen an); Komponenten ohne Plan bleiben Einmalkauf.
        if (wantsSubscription && item.plan) {
          line.selling_plan = Number(item.plan);
        }
        return line;
      });
  }

  async #addToCart() {
    const items = this.#buildItems();
    if (!items.length) return;

    this.button?.setAttribute('disabled', '');
    this.button?.classList.add('is-loading');

    const deferred = CartLinesUpdateEvent.createPromise?.();
    if (deferred) {
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((item) => ({
            merchandiseId: String(item.id),
            quantity: item.quantity,
          })),
          promise: deferred.promise,
        })
      );
    }

    try {
      const response = await fetch(Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const result = await response.json();

      if (result.status) {
        this.#showError(result.description || result.message);
        deferred?.reject?.(new Error(result.message));
        return;
      }

      const cartResponse = await fetch(`${Theme?.routes?.cart_url || '/cart'}.js`, {
        headers: { Accept: 'application/json' },
      });
      const cart = await cartResponse.json();

      if (deferred) {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'alp-bundle-add',
            itemCount: items.length,
            didError: false,
          },
        });
      } else {
        window.location.href = Theme?.routes?.cart_url || '/cart';
      }
    } catch (error) {
      this.#showError();
      deferred?.reject?.(error);
    } finally {
      this.button?.removeAttribute('disabled');
      this.button?.classList.remove('is-loading');
    }
  }

  #showError(message) {
    const target = this.querySelector('[data-bundle-add-error]');
    if (!target) return;
    target.textContent = message || target.dataset.fallback || '';
    target.hidden = false;
  }
}

if (!customElements.get('alp-bundle-add')) {
  customElements.define('alp-bundle-add', BundleAddMulti);
}
